import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Link } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { PaperData } from './types';
import RelevanceBadge from './RelevanceBadge';
import { responsive } from '../../lib/responsive';
import { getSourceLogoByName } from '../../lib/sourceLogos';
import { getPaperVenueLabel } from '../../lib/sourceHelpers';
import { formatPaperAuthors } from '../../lib/paperAuthors';
import {
    DownvoteReason,
    FeedbackVote,
    ProjectPaperFeedback,
} from '../../lib/projectPaperFeedback';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { type LocalizedMessage, useResolvedMessage } from '../../lib/i18n/messages';

interface PaperCardProps {
    paper: PaperData;
    linkHref: string;
    showRank?: boolean;
    rankPosition?: number;
    showScores?: boolean;
    relevanceScore?: number | null;
    relevanceClass?: number | null;
    showSummary?: boolean;
    summaryContent?: string | null;
    paperzillaTitle?: string | null;
    eli5?: string | null;
    showRating?: boolean;
    projectPaperId?: string;
    feedback?: ProjectPaperFeedback | null;
    onSetFeedback?: (vote: FeedbackVote, reason?: DownvoteReason) => Promise<void>;
    onClearFeedback?: () => Promise<void>;
    feedbackPending?: boolean;
}

function formatAuthors(authors: PaperData['authors']) {
    return formatPaperAuthors(authors, 'Unknown authors');
}

function truncateText(text: string | null | undefined, maxLength: number) {
    if (!text) {
        return '';
    }

    if (text.length <= maxLength) {
        return text;
    }

    return `${text.substring(0, maxLength)}...`;
}

function splitIntoSentences(text: string | null | undefined) {
    if (!text) {
        return [];
    }

    return text
        .replace(/\s+/g, ' ')
        .trim()
        .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
        .map((sentence) => sentence.trim())
        .filter(Boolean);
}

function RatingButton({
    icon,
    label,
    isActive,
    disabled,
    color,
    backgroundColor,
    textColor,
    onPress,
}: {
    icon: 'heart' | 'heart-circle-bolt' | 'thumbs-down';
    label: LocalizedMessage;
    isActive: boolean;
    disabled?: boolean;
    color: string;
    backgroundColor: string;
    textColor: string;
    onPress: () => void;
}) {
    const resolvedLabel = useResolvedMessage(label);
    return (
        <TouchableOpacity
            style={[
                styles.ratingButton,
                isActive && { backgroundColor },
                disabled && styles.ratingButtonDisabled,
            ]}
            onPress={onPress}
            activeOpacity={0.7}
            disabled={disabled}
        >
            <FontAwesome6
                name={icon}
                size={14}
                color={isActive ? color : colors.text500}
                solid={isActive}
            />
            <Text
                style={[
                    styles.ratingButtonText,
                    isActive && { color: textColor },
                ]}
            >
                {resolvedLabel}
            </Text>
        </TouchableOpacity>
    );
}

export default function PaperCard({
    paper,
    linkHref,
    showScores = false,
    relevanceScore,
    relevanceClass,
    showSummary = false,
    summaryContent,
    eli5,
    showRating = false,
    projectPaperId,
    feedback = null,
    onSetFeedback,
    onClearFeedback,
    feedbackPending = false,
}: PaperCardProps) {
    const { fontsLoaded } = useFontLoading();
    const [showReasonChips, setShowReasonChips] = useState(false);
    const [showAbstract, setShowAbstract] = useState(false);

    const showFeedbackControls = Boolean(showRating && projectPaperId && onSetFeedback && onClearFeedback);
    const venueLabel = useMemo(() => getPaperVenueLabel({
        sourceId: paper.source_id,
        sourcePaperId: paper.source_paper_id,
        doi: paper.doi,
        url: paper.url,
    }), [paper.doi, paper.source_id, paper.source_paper_id, paper.url]);
    const sourceLogo = paper.source_id ? getSourceLogoByName(venueLabel) : null;
    const abstractSentences = useMemo(() => splitIntoSentences(paper.abstract), [paper.abstract]);
    const currentVote = feedback?.vote ?? null;
    const currentReason = feedback?.downvote_reason ?? null;

    useEffect(() => {
        if (currentVote !== 'downvote' || currentReason) {
            setShowReasonChips(false);
        }
    }, [currentReason, currentVote]);

    const handleLike = async () => {
        if (!showFeedbackControls || feedbackPending) {
            return;
        }

        setShowReasonChips(false);

        if (currentVote === 'upvote') {
            await onClearFeedback?.();
            return;
        }

        await onSetFeedback?.('upvote', null);
    };

    const handleStar = async () => {
        if (!showFeedbackControls || feedbackPending) {
            return;
        }

        setShowReasonChips(false);

        if (currentVote === 'star') {
            await onClearFeedback?.();
            return;
        }

        await onSetFeedback?.('star', null);
    };

    const handleDislike = async () => {
        if (!showFeedbackControls || feedbackPending) {
            return;
        }

        if (currentVote === 'downvote') {
            setShowReasonChips(false);
            await onClearFeedback?.();
            return;
        }

        setShowReasonChips(true);

        try {
            await onSetFeedback?.('downvote', null);
        } catch {
            setShowReasonChips(false);
        }
    };

    const handleReasonSelect = async (reason: DownvoteReason) => {
        if (!showFeedbackControls || !reason || feedbackPending) {
            return;
        }

        try {
            await onSetFeedback?.('downvote', reason);
            setShowReasonChips(false);
        } catch {
            // Keep the picker open so the user can retry.
        }
    };

    const cardInner = (
        <>
            {showFeedbackControls ? (
                <Link href={linkHref} asChild>
                    <TouchableOpacity
                        style={styles.feedbackArrowButton}
                        activeOpacity={0.7}
                        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                    >
                        <FontAwesome6 name="circle-arrow-right" size={24} color={colors.primaryGreen} solid />
                    </TouchableOpacity>
                </Link>
            ) : (
                <View style={styles.arrowIconContainer}>
                    <FontAwesome6 name="circle-arrow-right" size={24} color={colors.primaryGreen} solid />
                </View>
            )}

            <View style={styles.paperContent} {...responsive('paper-card-content')}>
                <View style={styles.titleRow}>
                    {sourceLogo && (
                        <View style={styles.sourceIconContainer}>
                            <Image source={sourceLogo} style={styles.sourceIcon} resizeMode="contain" />
                        </View>
                    )}

                    {showFeedbackControls ? (
                        <Link href={linkHref} asChild>
                            <TouchableOpacity style={styles.titleLinkButton} activeOpacity={0.7}>
                                <Text
                                    style={[
                                        styles.paperTitle,
                                        fontsLoaded && interSemiBold,
                                    ]}
                                    numberOfLines={2}
                                >
                                    {paper.title}
                                </Text>
                            </TouchableOpacity>
                        </Link>
                    ) : (
                        <Text
                            style={[
                                styles.paperTitle,
                                fontsLoaded && interSemiBold,
                            ]}
                            numberOfLines={2}
                        >
                            {paper.title}
                        </Text>
                    )}
                </View>

                <Text
                    style={[
                        styles.authors,
                        fontsLoaded && interRegular,
                    ]}
                    numberOfLines={1}
                >
                    {formatAuthors(paper.authors)}
                </Text>

                {eli5 && (
                    <Text
                        style={[
                            styles.eli5Text,
                            fontsLoaded && interRegular,
                        ]}
                    >
                        {eli5}
                    </Text>
                )}

                {showSummary && summaryContent && (
                    <Text
                        style={[
                            styles.summaryText,
                            fontsLoaded && interRegular,
                        ]}
                        numberOfLines={3}
                    >
                        {truncateText(summaryContent, 180)}
                    </Text>
                )}

                <View style={styles.footer}>
                    <View style={styles.metadataRow} {...responsive('paper-card-metadata')}>
                        {venueLabel && (
                            <View style={styles.metadataItem}>
                                <Text
                                    style={[
                                        styles.metadataLabel,
                                        fontsLoaded && interRegular,
                                    ]}
                                ><Trans id="papers.papercard.source" comment="UI copy in components/papers/PaperCard.tsx">
                                    Source:
                                </Trans></Text>
                                <Text
                                    style={[
                                        styles.metadataValue,
                                        fontsLoaded && interRegular,
                                    ]}
                                >
                                    {venueLabel}
                                </Text>
                            </View>
                        )}

                        {showFeedbackControls && paper.abstract && (
                            <TouchableOpacity
                                style={styles.abstractToggle}
                                onPress={() => setShowAbstract((current) => !current)}
                                activeOpacity={0.7}
                            >
                                <Text
                                    style={[
                                        styles.abstractToggleText,
                                        fontsLoaded && interSemiBold,
                                    ]}
                                >
                                    {showAbstract ? <Trans id="papers.papercard.hideAbstract" comment="Conditional UI copy in components/papers/PaperCard.tsx">Hide abstract</Trans> : <Trans id="papers.papercard.showAbstract" comment="Conditional UI copy in components/papers/PaperCard.tsx">Show abstract</Trans>}
                                </Text>
                                <FontAwesome6
                                    name={showAbstract ? 'chevron-up' : 'chevron-down'}
                                    size={11}
                                    color={colors.primaryGreen}
                                />
                            </TouchableOpacity>
                        )}

                        {showScores && relevanceClass !== null && relevanceClass !== undefined && (
                            <RelevanceBadge relevanceClass={relevanceClass} size="compact" />
                        )}

                        {showScores && relevanceScore !== null && relevanceScore !== undefined && (
                            <View style={styles.metadataItem}>
                                <FontAwesome6 name="compass" size={12} color={colors.text700} />
                                <Text
                                    style={[
                                        styles.metadataLabel,
                                        fontsLoaded && interSemiBold,
                                    ]}
                                ><Trans id="papers.papercard.relevance" comment="UI copy in components/papers/PaperCard.tsx">
                                    Relevance:
                                </Trans></Text>
                                <Text
                                    style={[
                                        styles.metadataValue,
                                        fontsLoaded && interRegular,
                                    ]}
                                >
                                    {Math.round(relevanceScore * 100)}%
                                </Text>
                            </View>
                        )}
                    </View>
                </View>

                {showFeedbackControls && showAbstract && paper.abstract && (
                    <View style={styles.abstractPanel}>
                        {abstractSentences.length > 1 ? (
                            <View style={styles.abstractSentenceList}>
                                {abstractSentences.map((sentence, index) => (
                                    <Text
                                        key={`${index}-${sentence.slice(0, 24)}`}
                                        style={[
                                            styles.abstractSentence,
                                            fontsLoaded && interRegular,
                                        ]}
                                    >
                                        {sentence}
                                    </Text>
                                ))}
                            </View>
                        ) : (
                            <Text
                                style={[
                                    styles.abstractText,
                                    fontsLoaded && interRegular,
                                ]}
                            >
                                {paper.abstract}
                            </Text>
                        )}
                    </View>
                )}
            </View>
        </>
    );

    if (!showFeedbackControls) {
        return (
            <Link href={linkHref} asChild>
                <TouchableOpacity style={styles.paperCard} activeOpacity={0.7}>
                    {cardInner}
                </TouchableOpacity>
            </Link>
        );
    }

    return (
        <View style={styles.feedbackCard}>
            <View style={styles.paperCard}>
                {cardInner}
            </View>

            <View style={styles.feedbackStrip}>
                <RatingButton
                    icon="heart"
                    label={msg({ id: "papers.papercard.like", message: "Like", comment: "UI label in components/papers/PaperCard.tsx" })}
                    isActive={currentVote === 'upvote'}
                    disabled={feedbackPending}
                    color={colors.primaryGreen}
                    backgroundColor={colors.primary100}
                    textColor={colors.primaryGreenDark}
                    onPress={() => { void handleLike(); }}
                />
                <View style={styles.feedbackDivider} />
                <RatingButton
                    icon="heart-circle-bolt"
                    label={msg({ id: "papers.papercard.superlike", message: "Superlike", comment: "UI label in components/papers/PaperCard.tsx" })}
                    isActive={currentVote === 'star'}
                    disabled={feedbackPending}
                    color="#b7791f"
                    backgroundColor="#fff7d6"
                    textColor="#8a5a00"
                    onPress={() => { void handleStar(); }}
                />
                <View style={styles.feedbackDivider} />
                <RatingButton
                    icon="thumbs-down"
                    label={msg({ id: "papers.papercard.dislike", message: "Dislike", comment: "UI label in components/papers/PaperCard.tsx" })}
                    isActive={currentVote === 'downvote'}
                    disabled={feedbackPending}
                    color="#991b1b"
                    backgroundColor="#fee2e2"
                    textColor="#991b1b"
                    onPress={() => { void handleDislike(); }}
                />
                {feedbackPending && (
                    <View style={styles.feedbackPending}>
                        <ActivityIndicator size="small" color={colors.primaryGreen} />
                    </View>
                )}
            </View>

            {showReasonChips && (
                <View style={styles.reasonRow}>
                    <TouchableOpacity
                        style={[
                            styles.reasonChip,
                            currentReason === 'not_relevant' && styles.reasonChipActive,
                        ]}
                        onPress={() => { void handleReasonSelect('not_relevant'); }}
                        activeOpacity={0.7}
                        disabled={feedbackPending}
                    >
                        <Text
                            style={[
                                styles.reasonChipText,
                                currentReason === 'not_relevant' && styles.reasonChipTextActive,
                                fontsLoaded && interSemiBold,
                            ]}
                        ><Trans id="papers.papercard.notRelevant" comment="UI copy in components/papers/PaperCard.tsx">
                            Not relevant
                        </Trans></Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[
                            styles.reasonChip,
                            currentReason === 'low_quality' && styles.reasonChipActive,
                        ]}
                        onPress={() => { void handleReasonSelect('low_quality'); }}
                        activeOpacity={0.7}
                        disabled={feedbackPending}
                    >
                        <Text
                            style={[
                                styles.reasonChipText,
                                currentReason === 'low_quality' && styles.reasonChipTextActive,
                                fontsLoaded && interSemiBold,
                            ]}
                        ><Trans id="papers.papercard.lowQuality" comment="UI copy in components/papers/PaperCard.tsx">
                            Low quality
                        </Trans></Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={styles.dismissReasonButton}
                        onPress={() => setShowReasonChips(false)}
                        activeOpacity={0.7}
                        disabled={feedbackPending}
                    >
                        <FontAwesome6 name="xmark" size={14} color={colors.text500} />
                    </TouchableOpacity>
                </View>
            )}

            {!showReasonChips && currentVote === 'downvote' && (
                <View style={styles.reasonSummaryRow}>
                    <TouchableOpacity
                        style={styles.reasonSummaryChip}
                        onPress={() => setShowReasonChips(true)}
                        activeOpacity={0.7}
                        disabled={feedbackPending}
                    >
                        <Text
                            style={[
                                styles.reasonSummaryText,
                                currentReason && styles.reasonSummaryTextActive,
                                fontsLoaded && interSemiBold,
                            ]}
                        >
                            {currentReason === 'not_relevant'
                                ? <Trans id="papers.papercard.reasonNotRelevant" comment="Conditional UI copy in components/papers/PaperCard.tsx">Reason: Not relevant</Trans>
                                : currentReason === 'low_quality'
                                    ? <Trans id="papers.papercard.reasonLowQuality" comment="Conditional UI copy in components/papers/PaperCard.tsx">Reason: Low quality</Trans>
                                    : <Trans id="papers.papercard.addReason" comment="Conditional UI copy in components/papers/PaperCard.tsx">Add reason</Trans>}
                        </Text>
                    </TouchableOpacity>
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    feedbackCard: {
        borderRadius: 12,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.card,
    },
    paperCard: {
        position: 'relative',
        backgroundColor: colors.card,
        borderRadius: 12,
        padding: 16,
    },
    arrowIconContainer: {
        position: 'absolute',
        bottom: 12,
        right: 12,
        zIndex: 1,
    },
    feedbackArrowButton: {
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 1,
    },
    paperContent: {
        paddingRight: 44,
        paddingBottom: 4,
        gap: 8,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
    },
    titleLinkButton: {
        flex: 1,
    },
    sourceIconContainer: {
        width: 24,
        height: 24,
        flexShrink: 0,
    },
    sourceIcon: {
        width: '100%',
        height: '100%',
    },
    paperTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text900,
        lineHeight: 22,
        flex: 1,
    },
    authors: {
        fontSize: 14,
        color: colors.text700,
    },
    eli5Text: {
        fontSize: 14,
        color: colors.text700,
        lineHeight: 21,
        marginTop: 4,
    },
    summaryText: {
        fontSize: 13,
        color: colors.text500,
        lineHeight: 19,
    },
    footer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 4,
    },
    metadataRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    metadataItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    abstractToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    abstractToggleText: {
        fontSize: 12,
        color: colors.primaryGreen,
        fontWeight: '600',
    },
    metadataLabel: {
        fontSize: 12,
        color: colors.text700,
    },
    metadataValue: {
        fontSize: 12,
        color: colors.text700,
    },
    abstractPanel: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    abstractText: {
        fontSize: 13,
        color: colors.text700,
        lineHeight: 20,
    },
    abstractSentenceList: {
        gap: 8,
    },
    abstractSentence: {
        fontSize: 13,
        color: colors.text700,
        lineHeight: 20,
    },
    feedbackStrip: {
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'stretch',
        borderTopWidth: 1,
        borderTopColor: colors.border,
        backgroundColor: '#fbfcfe',
    },
    feedbackDivider: {
        width: 1,
        backgroundColor: colors.border,
    },
    ratingButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 12,
        paddingHorizontal: 10,
    },
    ratingButtonDisabled: {
        opacity: 0.65,
    },
    ratingButtonText: {
        fontSize: 12,
        color: colors.text500,
        fontWeight: '600',
    },
    feedbackPending: {
        position: 'absolute',
        right: 12,
        top: 10,
    },
    reasonRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        backgroundColor: colors.bgWeak,
    },
    reasonChip: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.card,
    },
    reasonChipActive: {
        backgroundColor: '#fee2e2',
        borderColor: '#fecaca',
    },
    reasonChipText: {
        fontSize: 12,
        color: colors.text700,
    },
    reasonChipTextActive: {
        color: '#991b1b',
    },
    dismissReasonButton: {
        marginLeft: 'auto',
        width: 30,
        height: 30,
        borderRadius: 15,
        alignItems: 'center',
        justifyContent: 'center',
    },
    reasonSummaryRow: {
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        backgroundColor: colors.bgWeak,
    },
    reasonSummaryChip: {
        alignSelf: 'flex-start',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: '#fecaca',
        backgroundColor: '#fff1f2',
    },
    reasonSummaryText: {
        fontSize: 12,
        color: '#991b1b',
    },
    reasonSummaryTextActive: {
        color: '#7f1d1d',
    },
});
